<script setup lang="ts">
import { MAPBOX_URL } from '@@/shared/mapbox-referential';
import { MARKER_COLOR, getIsValidPosition } from '~/composables/mapbox/map';

const props = withDefaults(defineProps<{
  latitude?: number | string
  longitude?: number | string
  displayPin?: boolean
}>(), {
  displayPin: false,
});

const runtimeConfig = useRuntimeConfig();
const MAPBOX_TOKEN = runtimeConfig.public.mapboxToken;
const SIZES = {
  small: `358x358`,
  medium: `775x358`,
} as const;

const imgSrcSet = computed(() => {
  // conversion to Number is need for encoding “marker” data
  const latitudeNumber = typeof props.latitude === `string` ? Number.parseFloat(props.latitude) : props.latitude;
  const longitudeNumber = typeof props.longitude === `string` ? Number.parseFloat(props.longitude) : props.longitude;
  if (!getIsValidPosition([latitudeNumber, longitudeNumber])) return false;

  const COORD_AND_ZOOM = `${longitudeNumber},${latitudeNumber},15`;

  const marker = {
    type: `FeatureCollection`,
    features: [{
      type: `Feature`,
      properties: { 'marker-color': MARKER_COLOR, 'marker-size': `medium` },
      // This NEED to be numbers
      // Encoding strings will result in wrong `encodeURIComponent` result
      geometry: { type: `Point`, coordinates: [longitudeNumber, latitudeNumber] },
    }],
  };

  // JSON needs to be encoded by encodeURIComponent
  // TODO: add GEO_JSON only if displayPin is true
  const GEO_JSON = `geojson(${encodeURIComponent(JSON.stringify(marker))})`;

  const srcsetUrl: Record<'small' | 'medium', string> = Object.fromEntries(
    Object.entries(SIZES).map(
      ([key, size]) => {
        const mapboxImagePath = [
          `/styles/v1/mapbox/streets-v12/static`,
          props.displayPin ? GEO_JSON : '',
          COORD_AND_ZOOM,
          `${size}@2x`,
        ].filter(Boolean).join('/');
        const mapboxStaticImageUrl = new URL(mapboxImagePath, MAPBOX_URL);
        mapboxStaticImageUrl.searchParams.append('access_token', MAPBOX_TOKEN);
        return [key as 'small' | 'medium', mapboxStaticImageUrl.toString()];
      },
    )) as Record<'small' | 'medium', string>;

  return srcsetUrl;
});
</script>

<template>
  <picture v-if="imgSrcSet">
    <source
      media="(max-width: 480px)"
      :srcset="imgSrcSet.small"
    >
    <source
      media="(min-width: 481px)"
      :srcset="imgSrcSet.medium"
    >
    <img
      :src="imgSrcSet.medium"
      class="block rounded-xl"
      width="775px"
      height="358px"
    >
  </picture>
  <IadMapDefaultPlaceholder
    v-else
    class="h-93 w-full"
  />
</template>
